import {Avatar} from '@dropbox/dig-components/avatar';
import {Button} from '@dropbox/dig-components/buttons';
import {LabelGroup} from '@dropbox/dig-components/combinations';
import {List} from '@dropbox/dig-components/list';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkCircleFill, SearchLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import axios from 'axios';
import {Dropboxer as DropboxerResponse} from 'client';
import {useSelectIndividualCallback} from 'components/feedback_chat/chat_hooks';
import {useThrottleAsyncCallback} from 'hooks/use_throttle';
import {useCallback, useMemo, useState} from 'react';
import {useAppSelector} from 'store';
import {
  getDropboxersFromSessions,
  getFeedbackChatState,
} from 'store/features/feedback_chat/selectors';
import {Dropboxer, Dropboxers} from 'store/features/feedback_chat/types';
import {API_URL, getDropboxService} from 'utilities';

import styles from './feedback_recipients_sidebar.module.css';

interface FeedbackRequestEntry {
  employee_id: string;
  preferred_first_name: string;
  preferred_last_name: string;
  job_title: string;
  job_level: string;
  status: string;
}

interface FeedbackRequestsResponse {
  feedback_requests: FeedbackRequestEntry[];
}

const useGetFeedbackRequests = () => {
  return useQuery<FeedbackRequestsResponse>({
    queryKey: ['/api/v1/feedback-requests'],
    queryFn: async () => {
      const response = await axios.get<FeedbackRequestsResponse>(
        `${API_URL}/api/v1/feedback-requests/`,
        {withCredentials: true}
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const FeedbackSubmittedInterstitial = ({
  name,
  onChatAnyway,
}: {
  name: string;
  onChatAnyway: () => void;
}) => {
  return (
    <div className={styles['submitted-container']}>
      <UIIcon
        src={CheckmarkCircleFill}
        className={styles['submitted-icon']}
        size="large"
      />
      <Text variant="label" tagName="h2" size="large" isBold>
        Feedback Submitted
      </Text>
      <Text
        variant="paragraph"
        tagName="p"
        color="subtle"
        className={styles['submitted-subtext']}
      >
        {`You've already provided feedback for ${name} this cycle. Thank you for your contribution!`}
      </Text>
      <Button variant="outline" onClick={onChatAnyway}>
        Chat Anyway
      </Button>
    </div>
  );
};

export const FeedbackRecipientsSidebar = ({
  onSelectRecipient,
  onSelectDropboxer,
  onSelectActiveChat,
  onShowInterstitial,
  selectedEmployeeId,
}: {
  onSelectRecipient: (employeeId: string, entry: FeedbackRequestEntry) => void;
  onSelectDropboxer: (dropboxer: DropboxerResponse) => void;
  onSelectActiveChat: (employeeId: string, dropboxer: Dropboxer) => void;
  onShowInterstitial: (entry: FeedbackRequestEntry) => void;
  selectedEmployeeId?: string;
}) => {
  const {data, isLoading, isError} = useGetFeedbackRequests();
  const [feedbackRequestSearchQuery, setFeedbackRequestSearchQuery] =
    useState('');
  const [dropboxerSearchQuery, setDropboxerSearchQuery] = useState('');
  const [dropboxerSearchResults, setDropboxerSearchResults] = useState<
    DropboxerResponse[]
  >([]);
  const [isDropboxerSearching, setIsDropboxerSearching] = useState(false);
  const dropboxersWithSessions: Dropboxers = useAppSelector(
    getDropboxersFromSessions
  );

  const isDropboxerSearchActive = dropboxerSearchQuery.length >= 3;

  const feedbackRecipientIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of data?.feedback_requests ?? []) {
      ids.add(entry.employee_id);
    }
    return ids;
  }, [data]);

  const filteredRecipients = useMemo(() => {
    const entries = data?.feedback_requests ?? [];
    if (!feedbackRequestSearchQuery) return entries;
    const query = feedbackRequestSearchQuery.toLowerCase();
    return entries.filter((entry) => {
      const fullName =
        `${entry.preferred_first_name} ${entry.preferred_last_name}`.toLowerCase();
      return fullName.includes(query);
    });
  }, [data, feedbackRequestSearchQuery]);

  const activeChats = useMemo(() => {
    if (isDropboxerSearchActive) return [];
    return Object.entries(dropboxersWithSessions)
      .filter(
        ([id, dropboxer]) =>
          !feedbackRecipientIds.has(id) &&
          dropboxer.preferred_first_name !== undefined
      )
      .map(([id, dropboxer]) => ({id, dropboxer}));
  }, [dropboxersWithSessions, feedbackRecipientIds, isDropboxerSearchActive]);

  const otherSearchResults = useMemo(() => {
    if (!isDropboxerSearchActive) return [];
    return dropboxerSearchResults.filter(
      (dropboxer) => !feedbackRecipientIds.has(dropboxer.employee_id)
    );
  }, [dropboxerSearchResults, feedbackRecipientIds, isDropboxerSearchActive]);

  const handleDropboxerSearch = useCallback(async (query: string) => {
    if (query.length < 3) return;
    setIsDropboxerSearching(true);
    try {
      const response =
        await getDropboxService().searchDropboxerByNameOrEmailApiV1DropboxerSearchGet(
          query,
          5
        );
      setDropboxerSearchResults(response.dropboxers);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDropboxerSearching(false);
    }
  }, []);

  const handleDropboxerSearchThrottled = useThrottleAsyncCallback(
    handleDropboxerSearch,
    1000
  );

  const handleDropboxerQueryChange = useCallback(
    (query: string) => {
      setDropboxerSearchQuery(query);
      if (query.length < 3) {
        setDropboxerSearchResults([]);
        return;
      }
      handleDropboxerSearchThrottled(query);
    },
    [handleDropboxerSearchThrottled]
  );

  const hasNoMatchingFeedbackRequests =
    !isLoading &&
    !isError &&
    filteredRecipients.length === 0 &&
    feedbackRequestSearchQuery !== '' &&
    (data?.feedback_requests?.length ?? 0) > 0;

  const hasEmptyFeedbackRequests =
    !isLoading && !isError && (data?.feedback_requests?.length ?? 0) === 0;

  const hasNoDropboxerBrowseResults =
    !isDropboxerSearching &&
    !isDropboxerSearchActive &&
    activeChats.length === 0;

  const hasNoDropboxerSearchResults =
    !isDropboxerSearching &&
    isDropboxerSearchActive &&
    otherSearchResults.length === 0;

  return (
    <div className={styles['sidebar']}>
      {/* <Text
        variant="label"
        tagName="div"
        size="small"
        isBold
        color="subtle"
        className={styles['sidebar-header']}
      >
        Feedback Recipients
      </Text> */}

      <div className={styles['feedback-requests-section']}>
        <Text
          variant="label"
          tagName="div"
          size="small"
          isBold
          color="subtle"
          className={styles['section-header']}
        >
          Feedback Requests
        </Text>

        <div className={styles['search-container']}>
          <TextInput
            placeholder="Search feedback requests..."
            size="small"
            value={feedbackRequestSearchQuery}
            onChange={(e) => setFeedbackRequestSearchQuery(e.target.value)}
            withRightAccessory={<UIIcon src={SearchLine} size="small" />}
          />
        </div>

        <div className={styles['section-list']}>
          {isLoading && (
            <div className={styles['spinner-container']}>
              <Spinner size="small" />
            </div>
          )}

          {isError && (
            <div className={styles['empty-state']}>
              <Text variant="paragraph" tagName="p" size="small" color="subtle">
                Unable to load feedback requests.
              </Text>
            </div>
          )}

          {hasEmptyFeedbackRequests && (
            <div className={styles['empty-state']}>
              <Text variant="paragraph" tagName="p" size="small" color="subtle">
                No feedback requests found.
              </Text>
            </div>
          )}

          {hasNoMatchingFeedbackRequests && (
            <div className={styles['empty-state']}>
              <Text variant="paragraph" tagName="p" size="small" color="subtle">
                No matching feedback requests.
              </Text>
            </div>
          )}

          {filteredRecipients.length > 0 && (
            <List isSelectable>
              {filteredRecipients.map((entry) => {
                const fullName = `${entry.preferred_first_name} ${entry.preferred_last_name}`;
                const isComplete = entry.status === 'Complete';

                const nameLabel = (
                  <span className={styles['name-with-check']}>
                    {fullName}
                    {isComplete && (
                      <UIIcon
                        src={CheckmarkCircleFill}
                        className={styles['checkmark-icon']}
                        size="small"
                      />
                    )}
                  </span>
                );

                return (
                  <List.Item
                    key={entry.employee_id}
                    className={styles['recipient-item']}
                    isSelected={selectedEmployeeId === entry.employee_id}
                    onClick={() => {
                      if (isComplete) {
                        onShowInterstitial(entry);
                      } else {
                        onSelectRecipient(entry.employee_id, entry);
                      }
                    }}
                  >
                    <List.Accessory>
                      <Avatar hasNoOutline size="small">
                        {`${entry.preferred_first_name[0]}${entry.preferred_last_name[0]}`}
                      </Avatar>
                    </List.Accessory>
                    <List.Content>
                      <LabelGroup
                        withText={nameLabel}
                        withSubtext={entry.job_title}
                      />
                    </List.Content>
                  </List.Item>
                );
              })}
            </List>
          )}
        </div>
      </div>

      <div className={styles['dropboxers-section']}>
        <Text
          variant="label"
          tagName="div"
          size="small"
          isBold
          color="subtle"
          className={`${styles['section-header']} ${styles['section-header-divider']}`}
        >
          Dropboxers
        </Text>

        <div className={styles['search-container']}>
          <TextInput
            placeholder="Search for a Dropboxer..."
            size="small"
            value={dropboxerSearchQuery}
            onChange={(e) => handleDropboxerQueryChange(e.target.value)}
            withRightAccessory={<UIIcon src={SearchLine} size="small" />}
          />
        </div>

        <div className={styles['section-list']}>
          {isDropboxerSearching && (
            <div className={styles['spinner-container']}>
              <Spinner size="small" />
            </div>
          )}

          {hasNoDropboxerBrowseResults && (
            <div className={styles['empty-state']}>
              <Text variant="paragraph" tagName="p" size="small" color="subtle">
                No active chats.
              </Text>
            </div>
          )}

          {hasNoDropboxerSearchResults && (
            <div className={styles['empty-state']}>
              <Text variant="paragraph" tagName="p" size="small" color="subtle">
                No results found.
              </Text>
            </div>
          )}

          {activeChats.length > 0 && (
            <>
              <Text
                variant="label"
                tagName="div"
                size="small"
                isBold
                color="subtle"
                className={styles['subsection-header']}
              >
                Other Active Chats
              </Text>
              <List isSelectable>
                {activeChats.map(({id, dropboxer}) => (
                  <List.Item
                    key={id}
                    className={styles['recipient-item']}
                    isSelected={selectedEmployeeId === id}
                    onClick={() => onSelectActiveChat(id, dropboxer)}
                  >
                    <List.Accessory>
                      <Avatar hasNoOutline size="small">
                        {`${dropboxer.preferred_first_name[0]}${dropboxer.preferred_last_name[0]}`}
                      </Avatar>
                    </List.Accessory>
                    <List.Content>
                      <LabelGroup
                        withText={`${dropboxer.preferred_first_name} ${dropboxer.preferred_last_name}`}
                        withSubtext={dropboxer.job_title}
                      />
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            </>
          )}

          {otherSearchResults.length > 0 && (
            <>
              <Text
                variant="label"
                tagName="div"
                size="small"
                isBold
                color="subtle"
                className={styles['subsection-header']}
              >
                Other Dropboxers
              </Text>
              <List isSelectable>
                {otherSearchResults.map((dropboxer) => (
                  <List.Item
                    key={dropboxer.employee_id}
                    className={styles['recipient-item']}
                    isSelected={selectedEmployeeId === dropboxer.employee_id}
                    onClick={() => onSelectDropboxer(dropboxer)}
                  >
                    <List.Accessory>
                      <Avatar hasNoOutline size="small">
                        {`${dropboxer.preferred_first_name[0]}${dropboxer.preferred_last_name[0]}`}
                      </Avatar>
                    </List.Accessory>
                    <List.Content>
                      <LabelGroup
                        withText={`${dropboxer.preferred_first_name} ${dropboxer.preferred_last_name}`}
                        withSubtext={dropboxer.job_title}
                      />
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const feedbackRequestEntryToDropboxer = (
  entry: FeedbackRequestEntry
): Dropboxer => ({
  workday_id: '',
  email: '',
  preferred_first_name: entry.preferred_first_name,
  preferred_last_name: entry.preferred_last_name,
  job_title: entry.job_title,
  job_level: entry.job_level,
  previous_job_level: '',
  previous_title: '',
  is_terminated: false,
  primary_manager_employee_id: null,
  is_namer: false,
});

export const useFeedbackRecipientsSidebarController = () => {
  const selectIndividual = useSelectIndividualCallback();
  const {selectedIndividualInfo} = useAppSelector(getFeedbackChatState);

  const [interstitialEntry, setInterstitialEntry] =
    useState<FeedbackRequestEntry | null>(null);

  const handleSelectRecipient = useCallback(
    (employeeId: string, entry: FeedbackRequestEntry) => {
      setInterstitialEntry(null);
      selectIndividual(employeeId, feedbackRequestEntryToDropboxer(entry));
    },
    [selectIndividual]
  );

  const handleSelectDropboxer = useCallback(
    (dropboxer: DropboxerResponse) => {
      setInterstitialEntry(null);
      const {employee_id, ...rest} = dropboxer;
      selectIndividual(employee_id, rest);
    },
    [selectIndividual]
  );

  const handleSelectActiveChat = useCallback(
    (employeeId: string, dropboxer: Dropboxer) => {
      setInterstitialEntry(null);
      selectIndividual(employeeId, dropboxer);
    },
    [selectIndividual]
  );

  const handleShowInterstitial = useCallback((entry: FeedbackRequestEntry) => {
    setInterstitialEntry(entry);
  }, []);

  const handleChatAnyway = useCallback(() => {
    if (!interstitialEntry) return;
    const entry = interstitialEntry;
    setInterstitialEntry(null);
    selectIndividual(entry.employee_id, feedbackRequestEntryToDropboxer(entry));
  }, [interstitialEntry, selectIndividual]);

  const selectedEmployeeId =
    interstitialEntry?.employee_id ?? selectedIndividualInfo?.individualId;

  const hasDropboxerSelected = selectedIndividualInfo != null;

  return {
    interstitialEntry,
    selectedEmployeeId,
    hasDropboxerSelected,
    handleSelectRecipient,
    handleSelectDropboxer,
    handleSelectActiveChat,
    handleShowInterstitial,
    handleChatAnyway,
  };
};
